import Box from '@mui/material/Box';
import { styled } from '@mui/material/styles';
import { TransportationModeEnum } from 'RestAPIs/travel-distances/types';
import { useEditPageContext } from 'components/shared/CRUD/components/InnerPage/EditPageContext';
import withReadModeField from 'components/shared/CRUD/components/InnerPage/components/withReadModeField';
import { DefaultReadModeComponent } from 'components/shared/CRUD/components/InnerPage/components/withReadModeFieldRenderers';
import {
  GridCompact6,
  GridContainer
} from 'components/shared/Grid/Grid.components';
import { useGetTravelCompensationType } from 'queries';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { convertArrangementTypeToOption } from '../../arrangementsUtils';
import {
  CodeExternField,
  CodeField,
  CompensationTypeIdFieldInner,
  DescriptionField,
  IsExternalField,
  TransportationModeField
} from '../FormFields';
import TravelLinesTable from './TravelLinesTable';

import type { FC } from 'react';
import type { Arrangement } from '../../types';

const PREFIX = 'ArrangementFormFields';

const classes = {
  root: `${PREFIX}-root`
};

const StyledBox = styled(Box)(() => ({
  [`&.${classes.root}`]: {
    flexDirection: 'column',
    flex: 1
  }
}));

interface Props {
  closeInnerView: () => void;
  rowData?: Arrangement;
}

const CodeFieldWithReadMode = withReadModeField(CodeField);
const CodeExternFieldWithReadMode = withReadModeField(CodeExternField);
const DescriptionFieldWithReadMode = withReadModeField(DescriptionField);
const CompensationTypeIdFieldWithReadMode = withReadModeField(
  CompensationTypeIdFieldInner
);
const TransportationModeFieldWithReadMode = withReadModeField(
  TransportationModeField
);

const RenderCompensationTypeIdReadMode: FC<{
  name: string;
  label: string;
  arrangementTypes?: Array<{ id: string; name: string }>;
}> = ({ name, label, arrangementTypes }) => {
  'use client';

  const { getValues } = useFormContext();

  const selectedArrangementType = arrangementTypes?.find(
    (arrangementType) => arrangementType.id === getValues(name)
  );

  return (
    <DefaultReadModeComponent
      name={name}
      label={label}
      value={selectedArrangementType?.name}
    />
  );
};

const RenderTransportationModeFieldReadMode: FC<{
  name: string;
}> = ({ name }) => {
  'use memo';

  const { t } = useTranslation('common');
  const { getValues } = useFormContext();

  const selectedTransportationMode = Object.values(TransportationModeEnum).find(
    (mode) => mode === getValues(name)
  );

  return (
    <DefaultReadModeComponent
      name={t(`travelDistances.transportationModes.${name}`)}
      label={t('travelDistances.transportationMode')}
      value={
        selectedTransportationMode
          ? t(
              `travelDistances.transportationModes.${selectedTransportationMode}`
            )
          : '-'
      }
    />
  );
};

export const ArrangementFormFields: FC<Props> = ({
  closeInnerView,
  rowData
}) => {
  'use memo';

  const { control } = useFormContext();
  const { isEditable } = useEditPageContext();
  const { t } = useTranslation('common');
  const { data: arrangementTypes } = useGetTravelCompensationType();

  return (
    <StyledBox display="flex" className={classes.root}>
      <Box mb={2.5} sx={{ width: '100%' }}>
        <GridContainer mb={2.5}>
          <GridCompact6>
            <CodeFieldWithReadMode
              name="code"
              label={t('adminPortal.arrangements.code')}
              control={control}
            />
          </GridCompact6>

          <GridCompact6>
            <CodeExternFieldWithReadMode
              name="codeExtern"
              label={t('adminPortal.arrangements.externalCode')}
              control={control}
            />
          </GridCompact6>

          <GridCompact6>
            <DescriptionFieldWithReadMode
              name="description"
              label={t('adminPortal.arrangements.description')}
              control={control}
              variant="outlined"
            />
          </GridCompact6>

          <GridCompact6>
            <CompensationTypeIdFieldWithReadMode
              name="compensationTypeId"
              label={t('adminPortal.arrangements.typeOfArrangement')}
              control={control}
              options={convertArrangementTypeToOption(arrangementTypes ?? [])}
              readModeRenderer={(props) => (
                <RenderCompensationTypeIdReadMode
                  {...props}
                  arrangementTypes={arrangementTypes}
                />
              )}
            />
          </GridCompact6>

          <GridCompact6>
            <TransportationModeFieldWithReadMode
              name="transportationMode"
              label={''}
              control={control}
              readModeRenderer={({ name }) => (
                <RenderTransportationModeFieldReadMode name={name} />
              )}
            />
          </GridCompact6>
        </GridContainer>

        <Box display="flex" gap={2.5}>
          <GridCompact6>
            <IsExternalField control={control} disabled={!isEditable} />
          </GridCompact6>
        </Box>
      </Box>
      <TravelLinesTable rowData={rowData} closeInnerView={closeInnerView} />
    </StyledBox>
  );
};
